import type { IntakeApi } from '../api/client';
import { consoleSink, fanOut, type AnalyticsEvent, type AnalyticsSink } from './analytics';
import { createEnquiryEventsSink } from './enquiryEvents';

/**
 * A sink whose code loads on demand (w7-02: the initial route's budget). Events wait in order
 * until it is in; if it can't load, they go nowhere, as a blocked tag would.
 */
export function lazySink(load: () => Promise<AnalyticsSink | null>): AnalyticsSink {
  let sink: AnalyticsSink | null = null;
  let queue: AnalyticsEvent[] | null = [];
  load().then(
    (s) => {
      sink = s;
      const waiting = queue ?? [];
      queue = null;
      if (s) for (const e of waiting) s.track(e);
    },
    () => (queue = null),
  );
  return {
    track(e) {
      if (sink) sink.track(e);
      else queue?.push(e);
    },
  };
}

type Env = Partial<ImportMetaEnv> & { DEV?: boolean };

/**
 * The app's analytics: the Intake API's `enquiry_events` always; GA4 / Google Ads / the Meta
 * pixel when their ids are set (and only once consent is granted, see consent.ts); the
 * console in dev with `VITE_ANALYTICS_DEBUG=1`.
 */
export function createAppSink(api: IntakeApi, env: Env = import.meta.env): AnalyticsSink {
  const sinks: AnalyticsSink[] = [createEnquiryEventsSink({ api })];
  // GA4, Google Ads and the Meta pixel: their own chunk (thirdParty.ts), only when an id is set.
  // Nothing reaches them before consent anyway; the loaded sinks read it straight away.
  if (env.VITE_GA4_ID || env.VITE_GOOGLE_ADS_ID)
    sinks.push(
      lazySink(() =>
        import('./thirdParty').then((m) =>
          m.createGtagSink({
            ga4Id: env.VITE_GA4_ID,
            adsId: env.VITE_GOOGLE_ADS_ID,
            adsConversions: m.parseConversions(env.VITE_GOOGLE_ADS_CONVERSIONS),
          }),
        ),
      ),
    );
  if (env.VITE_META_PIXEL_ID) {
    const pixelId = env.VITE_META_PIXEL_ID;
    sinks.push(lazySink(() => import('./thirdParty').then((m) => m.createMetaPixelSink({ pixelId }))));
  }
  if (env.DEV && env.VITE_ANALYTICS_DEBUG) sinks.push(consoleSink);
  return fanOut(...sinks);
}
